import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Copy, Mail, MessageCircle, Pencil, Send } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { useGatewayStore } from "@/stores/useGatewayStore";
import { useMailboxes } from "@/hooks/useMail";
import { useChannelEndpoints } from "@/hooks/useChannelEndpoints";
import { imChannelState, type ImChannelState } from "@/lib/channel-endpoint-state";
import { useChannelActivation } from "@/hooks/useChannelActivation";
import { getChannelDetails } from "@/api/lx-openclaw-config";
import { agentMailboxAddress } from "@/lib/contacts-logic";
import type { AssistantIdentity, SetupStatus } from "@/types/contacts";
import type { AgentSection } from "@/components/agent/AgentSurface";

/**
 * `AssistantBusinessCard` — the identity hero's contact chips (redesign
 * §3.1-bis, operator refinement #9). Stacked beside the avatar/name column
 * (2-column card, operator 2026-07-27), one chip per way the assistant is
 * reachable — its e-mail, its WhatsApp, its Telegram — and one tap from
 * editing each. Tapping a chip selects that channel's tab (where its editors
 * live); the S2 drawers/§3.9 dialog will make the tap open the editor
 * directly. This card REPLACED the per-tab `AgentChannelSummary` sub-header
 * (redundant once the hero carries the contacts).
 *
 * Honesty (§2.5, same rule as every status chip here): an IM chip says
 * "Conectado" ONLY when BOTH endpoints are closed (the §2-bis two-endpoint
 * rule, over the LIVE `useChannelEndpoints` probe — never `channelComplete`).
 * With endpoint 1 alone the chip keeps the handle — the assistant IS reachable
 * at @bot (operator, 2026-07-27) — but the badge reads "Etapa 1 de 2" and is
 * the one-touch into step 2 (operator triage 2026-09-11, S10: never
 * "Conectado" on a one-ended channel). While endpoint 1 is not live the row
 * reads "Conectar" and never a fabricated handle. The handle text itself comes
 * from `getChannelDetails` (the same source `AgentChannelSummary` uses), the
 * e-mail address from the provisioned-mailbox SoT.
 *
 * ⚠ The badge comes from **`imChannelState`**, not `imBadge` — THREE inputs,
 * not two (RETEST-1 I-15, 2026-09-11). Activation is the third: a token that is
 * stored but that the running config does not carry leaves endpoint 1 reading
 * down, which is indistinguishable from nothing-set-up — so this card offered
 * "Conectar" for a step the lawyer had already completed. `useChannelActivation`
 * supplies `requiresRoll`, and a **`savedNotActive`** chip names that state.
 * Its three-valued contract is load-bearing: only an explicit `true` produces
 * the new chip, so an UNKNOWN (an older runtime omits the optional field)
 * renders exactly as it did before the signal existed. That chip carries NO
 * action, for the same reason `ChannelStep1Notice`'s variant does not —
 * activation without a container roll has not shipped, so a control would
 * promise a path that cannot complete. Telegram-only by construction;
 * WhatsApp passes `null` and its answer is unchanged.
 *
 * Display-only. Degrades quietly: a missing handle / older runtime leaves the
 * row in its "Conectar" shape rather than erroring — it is orientation chrome.
 */

type CardChannel = "email" | "whatsapp" | "telegram";

const ROW_DEFS: { section: CardChannel; icon: LucideIcon; tint: string }[] = [
  {
    section: "email",
    icon: Mail,
    tint: "bg-indigo-100 text-indigo-600 dark:bg-indigo-950/60 dark:text-indigo-300",
  },
  {
    section: "whatsapp",
    icon: MessageCircle,
    tint: "bg-emerald-100 text-emerald-600 dark:bg-emerald-950/60 dark:text-emerald-300",
  },
  {
    section: "telegram",
    icon: Send,
    tint: "bg-sky-100 text-sky-600 dark:bg-sky-950/60 dark:text-sky-300",
  },
];

/** The IM handle from `config/channels/<id>/details` (account row, not liveness).
 *  Whatsapp → phone|displayName, Telegram → @username. */
function useImHandle(channel: "whatsapp" | "telegram"): string | null {
  const connectionState = useGatewayStore((s) => s.connectionState);
  const [handle, setHandle] = useState<string | null>(null);
  useEffect(() => {
    if (connectionState !== "connected") return;
    let cancelled = false;
    getChannelDetails(channel)
      .then((d) => {
        if (cancelled || !d) return;
        if (d.channelId === "telegram") {
          setHandle(d.identity?.username ? `@${d.identity.username}` : null);
        } else if (d.channelId === "whatsapp") {
          setHandle(d.identity?.phone ?? d.identity?.displayName ?? null);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [channel, connectionState]);
  return handle;
}

export function AssistantBusinessCard({
  assistant,
  onSelectSection,
  onEditChannel,
  onConnectChannel,
}: {
  assistant: AssistantIdentity | null;
  onSelectSection: (section: AgentSection) => void;
  /** Pencil → the channel's settings dialog (edit + gated disconnect). */
  onEditChannel: (channel: "email" | "telegram" | "whatsapp") => void;
  /** The one-touch [Conectar] (§3.5): the not-connected badge IS the button —
   *  one touch from the card into the benefit-led connect dialog. */
  onConnectChannel: (channel: "telegram" | "whatsapp") => void;
}) {
  const { t } = useTranslation();
  const rpc = useGatewayStore((s) => s.rpc);
  const connectionState = useGatewayStore((s) => s.connectionState);

  /** Which chip's copy just fired (✓ feedback for 1.5s). */
  const [copied, setCopied] = useState<CardChannel | null>(null);

  // E-mail (EP1 mailbox): address from the provisioned SoT, presence from the
  // mail surface — never the channel resolver (it hard-codes email to
  // disconnected). Poll-free.
  const [setup, setSetup] = useState<SetupStatus | null>(null);
  useEffect(() => {
    if (!rpc || connectionState !== "connected") return;
    let cancelled = false;
    rpc
      .setupStatus()
      .then((s) => !cancelled && setSetup(s))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [rpc, connectionState]);
  const { mailboxes, gate } = useMailboxes({ enabled: true });
  const emailAddress = agentMailboxAddress(setup?.items, assistant?.emails);
  const emailConfigured = gate === "enabled" && mailboxes.length > 0;

  // IMs: REACHABILITY (the handle, the copy control) follows ENDPOINT 1 — the
  // assistant's own door live (its bot / its WhatsApp link): a live bot with an
  // unpaired owner is still genuinely reachable at @bot (operator correction
  // 2026-07-27). The BADGE follows both endpoints PLUS activation
  // (`imChannelState` — not `imBadge`, see the header docblock): "Conectado"
  // only when the lawyer's command door is closed too, "Etapa 1 de 2" before
  // that (operator triage 2026-09-11 — the 07-27 correction's badge half is
  // superseded), and "Guardado, não ativo" when the token is stored but the
  // runtime is not running it. A slower poll than the connect dialog — the card
  // wants eventual freshness.
  const wa = useChannelEndpoints("whatsapp", { pollMs: 8000 });
  const tg = useChannelEndpoints("telegram", { pollMs: 8000 });
  const waHandle = useImHandle("whatsapp");
  const tgHandle = useImHandle("telegram");
  // ACTIVATION, the third signal (RETEST-1 I-15's card half): a stored token
  // the runtime is not running reads as endpoint-1-down, i.e. identical to
  // nothing-set-up, so the card offered "Conectar" for a step the lawyer had
  // already completed. Telegram-only by construction — `requiresRoll` exists on
  // no other channel's `/details` — so WhatsApp passes `null` and its answer is
  // byte-identical to the old `imBadge` one.
  // GATED to the one condition under which the answer is ever used:
  // `imChannelState` consults `requiresRoll` only when `imBadge` says
  // "disconnected", which is exactly `ep1 === false`. Ungated, this polled the
  // lexserver REST `/details` every 10 s for as long as the card was mounted —
  // including while Telegram was fully connected and the answer could not
  // matter. The sibling consumer (`ChannelPane`) gates its own call the same
  // way, so this also keeps the two surfaces consistent about when they ask.
  const tgActivation = useChannelActivation("telegram", { enabled: tg.ep1 === false });

  const state: Record<
    CardChannel,
    { connected: boolean | null; handle: string | null; badge: ImChannelState | null }
  > = {
    email: {
      connected: gate === "loading" ? null : emailConfigured,
      handle: emailAddress,
      badge: null,
    },
    whatsapp: { connected: wa.ep1, handle: waHandle, badge: imChannelState(wa.ep1, wa.ep2, null) },
    telegram: {
      connected: tg.ep1,
      handle: tgHandle,
      badge: imChannelState(tg.ep1, tg.ep2, tgActivation.requiresRoll),
    },
  };

  // CONTACT CHIPS stacked as the card's right column (operator refinements
  // 2026-07-27): each channel is a compact pill — tinted icon, the handle, its
  // status RIGHT BESIDE the handle (a soft dot+word when live, a small
  // "Conectar" badge when not), a pencil (tap = edit) and, when there is a
  // real handle, a COPY control (the old settings tab's copy buttons moved
  // here — that tab retired as redundant). Natural widths, left-aligned —
  // ragged-right like contact lines on a real printed card.
  return (
    <div
      data-testid="assistant-business-card"
      className="flex min-w-0 flex-col items-start gap-1"
    >
      {ROW_DEFS.map(({ section, icon: Icon, tint }) => {
        const { connected, handle, badge } = state[section];
        const label = t(`agent.sections.${section}`);
        // Connected → the handle (or the channel name if we have no handle yet);
        // not connected → the channel name, with a "Conectar" call to action.
        const primary = connected && handle ? handle : label;
        // Copy only what is actually displayed (honesty): the address / number
        // verbatim; Telegram as its shareable t.me link.
        const copyValue =
          connected && handle
            ? section === "telegram"
              ? `https://t.me/${handle.replace(/^@/, "")}`
              : handle
            : null;
        return (
          <div
            key={section}
            className="group flex max-w-full items-center gap-0.5 rounded-full border border-cream-200/80 bg-white/70 py-1 pl-1 pr-1.5 transition-all hover:border-cream-300 hover:bg-white hover:shadow-sm dark:border-coffee-700/70 dark:bg-coffee-900/60 dark:hover:border-coffee-600 dark:hover:bg-coffee-800"
          >
            <button
              type="button"
              data-testid={`assistant-card-row-${section}`}
              onClick={() => onSelectSection(section)}
              aria-label={t("agent.hero.card.openRow", { channel: label })}
              title={t("agent.hero.card.openRow", { channel: label })}
              className="flex min-w-0 items-center gap-1.5"
            >
              <span
                aria-hidden
                className={cn(
                  "flex h-6 w-6 shrink-0 items-center justify-center rounded-full",
                  tint,
                )}
              >
                <Icon size={13} />
              </span>
              <span className="max-w-[180px] truncate text-body-sm text-coffee-700 dark:text-cream-300">
                {primary}
              </span>
              {connected === true && (section === "email" || badge === "connected") ? (
                <span
                  data-testid="assistant-card-status"
                  data-state="connected"
                  className="flex shrink-0 items-center gap-1 text-[10px] uppercase tracking-wide text-emerald-700 dark:text-emerald-300"
                >
                  <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  {t(section === "email" ? "agent.hero.card.configured" : "agent.hero.card.connected")}
                </span>
              ) : null}
            </button>
            {connected === false && section !== "email" && badge !== "savedNotActive" && (
              // E-mail is auto-provisioned — it has no user "connect" flow, so
              // it never shows "Conectar". For the IMs the badge IS the
              // ONE-TOUCH button (§3.5): Telegram PRIMARY-styled (the starting
              // channel — §2.9 steering by prominence), WhatsApp secondary —
              // both one touch into the benefit-led connect dialog.
              <button
                type="button"
                data-testid="assistant-card-status"
                data-state="disconnected"
                onClick={() => onConnectChannel(section as "telegram" | "whatsapp")}
                className={cn(
                  "shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide transition-colors",
                  section === "telegram"
                    ? "bg-gold-500 text-coffee-900 hover:bg-gold-400 dark:text-coffee-950"
                    : "bg-gold-400/15 text-gold-700 hover:bg-gold-400/30 dark:bg-gold-500/15 dark:text-gold-400 dark:hover:bg-gold-500/30",
                )}
              >
                {t("agent.hero.card.connect")}
              </button>
            )}
            {badge === "savedNotActive" && (
              // SAVED, NOT ACTIVE YET (RETEST-1 I-15). Endpoint 1 reads down
              // because no plugin polls a token the running config does not
              // carry — so without this the card showed the nothing-set-up
              // "Conectar" and the lawyer believed his step was lost.
              //
              // NOT a button, deliberately, and for the same reason
              // `ChannelStep1Notice`'s own variant carries no action:
              // activation without a container roll has not shipped
              // (`ai-docs/telegram-activation-no-roll-miniplan.md`), so a
              // control here would promise a path that cannot complete. Amber
              // like the step-1 chip, never green — the two surfaces tell one
              // story (that notice's docblock asks for exactly this).
              <span
                data-testid="assistant-card-status"
                data-state="savedNotActive"
                title={t("agent.channels.savedNotActive")}
                className="flex shrink-0 items-center gap-1 rounded-full bg-amber-400/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800 dark:bg-amber-500/15 dark:text-amber-300"
              >
                <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                {t("agent.hero.card.savedNotActive")}
              </span>
            )}
            {connected === true && badge === "step1" && (
              // Endpoint 1 only (§2-bis): reachable, but not the lawyer's yet.
              // The badge names the step and is the one touch into step 2 —
              // the dialog's live flow opens straight on the pairing panel.
              <button
                type="button"
                data-testid="assistant-card-status"
                data-state="step1"
                onClick={() => onConnectChannel(section as "telegram" | "whatsapp")}
                aria-label={`${t("agent.hero.card.step1of2")} — ${t("agent.channels.step1PendingCta")}`}
                title={t("agent.channels.step1PendingCta")}
                className="flex shrink-0 items-center gap-1 rounded-full bg-amber-400/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800 transition-colors hover:bg-amber-400/30 dark:bg-amber-500/15 dark:text-amber-300 dark:hover:bg-amber-500/30"
              >
                <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                {t("agent.hero.card.step1of2")}
              </button>
            )}
            {/* The PENCIL is its own control: it opens the channel's settings
                dialog (edit + the typed-phrase-gated disconnect), while the
                chip body just jumps to the tab. */}
            <button
              type="button"
              data-testid={`assistant-card-edit-${section}`}
              onClick={() => onEditChannel(section)}
              aria-label={t("agent.hero.card.settingsTitle", { channel: label })}
              title={t("agent.hero.card.settingsTitle", { channel: label })}
              className="shrink-0 rounded-full p-1 text-cream-500 transition-colors hover:bg-cream-100 hover:text-coffee-700 dark:text-coffee-600 dark:hover:bg-coffee-800 dark:hover:text-cream-200"
            >
              <Pencil size={11} aria-hidden />
            </button>
            {copyValue && (
              <button
                type="button"
                data-testid={`assistant-card-copy-${section}`}
                onClick={() => {
                  void navigator.clipboard?.writeText(copyValue).then(() => {
                    setCopied(section);
                    window.setTimeout(
                      () => setCopied((c) => (c === section ? null : c)),
                      1500,
                    );
                  });
                }}
                aria-label={t("agent.hero.card.copy", { channel: label })}
                title={t("agent.hero.card.copy", { channel: label })}
                className="shrink-0 rounded-full p-1 text-cream-500 transition-colors hover:bg-cream-100 hover:text-coffee-700 dark:text-coffee-600 dark:hover:bg-coffee-800 dark:hover:text-cream-200"
              >
                {copied === section ? (
                  <Check size={11} aria-hidden className="text-emerald-600 dark:text-emerald-400" />
                ) : (
                  <Copy size={11} aria-hidden />
                )}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
